import type { OfferingV2Response } from '../../types/offeringsV2';
import type { PackageItemResponse } from '../../types/packages';
import { offeringFromPrice } from '../offers/offerUtils';
import type { PackagePhoneItem } from './PackagePhoneSheet';
import type { PackageFormItem } from './packageUtils';

/**
 * Phone-preview lines for the current form: choices come from the catalogue (always the form's
 * current state), prices from the server item when it has one (preview or saved package), else the
 * catalogue's "from" price. A service the catalogue no longer lists falls back to the server item.
 */
export function buildPhoneItems(
  items: PackageFormItem[],
  catalogue: Map<string, OfferingV2Response>,
  serverItems: PackageItemResponse[],
): PackagePhoneItem[] {
  const byId = new Map(serverItems.map((i) => [i.offeringId, i]));
  return items.map((item) => {
    const o = catalogue.get(item.offeringId);
    const s = byId.get(item.offeringId);
    if (!o) {
      return {
        offeringId: item.offeringId,
        name: s?.name ?? '—',
        nameAr: s?.nameAr ?? null,
        listPrice: s?.listPrice ?? null,
        fixedChoices: (s?.fixedChoices ?? []).map((f) => ({ label: f.label, labelAr: f.labelAr })),
        openVariations: s?.openVariations ?? [],
      };
    }
    const fixed = new Set(item.fixedValueIds);
    const variations = [...(o.variations ?? [])].sort((a, b) => a.sortOrder - b.sortOrder);
    return {
      offeringId: o.id,
      name: o.name,
      nameAr: s?.nameAr ?? null,
      listPrice: s?.listPrice ?? offeringFromPrice(o),
      fixedChoices: variations.flatMap((v) => (v.values ?? []).filter((val) => fixed.has(val.id))
        .map((val) => ({ label: val.label, labelAr: val.labelAr ?? null }))),
      openVariations: variations.filter((v) => !(v.values ?? []).some((val) => fixed.has(val.id)))
        .map((v) => ({ variationId: v.id, name: v.name, nameAr: v.nameAr ?? null, selectionMode: v.selectionMode ?? 'SINGLE' })),
    };
  });
}
